import DeleteIcon from "@mui/icons-material/Delete";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type CSSProperties, type FC, useCallback } from "react";
import { ThemedSvg } from "../../../../components/UIToolkit/ThemedSvg";
import { INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { Qualification } from "../../../../utils/models";

// Functional / Categorical: encodes qualification expiry urgency.
// Must remain stable across user themes.
const EXPIRY_COLORS = {
	EXPIRED: "#E85C5E",
	WARNING: "#F59023",
	VALID: "#009A0FCC",
} as const;

type ExpiryTextAndColor = {
	text: string;
	color: string;
};

const getExpiryStatus = (expires: DateTime | null): ExpiryTextAndColor => {
	if (!expires) {
		return {
			text: "No expiry date",
			color: INK.SECONDARY,
		};
	}
	const today = DateTime.now();
	if (expires < today) {
		return {
			text: `Expired ${expires.toJSDate().toLocaleDateString()}`,
			color: EXPIRY_COLORS.EXPIRED,
		};
	}
	if (expires.startOf("day") === today.startOf("day")) {
		return {
			text: "Expires today",
			color: EXPIRY_COLORS.EXPIRED,
		};
	}
	if (expires.diff(today, "day").days <= 30) {
		return {
			text: `Expires on ${expires.toJSDate().toLocaleDateString()}`,
			color: EXPIRY_COLORS.WARNING,
		};
	}
	return {
		text: `Valid until ${expires.toJSDate().toLocaleDateString()}`,
		color: EXPIRY_COLORS.VALID,
	};
};

type QualificationValidityProps = {
	expires: DateTime | null;
};

const QualificationValidity: FC<QualificationValidityProps> = ({ expires }) => {
	const expiryStatus = getExpiryStatus(expires);
	return (
		<Typography
			style={{
				fontWeight: 700,
				fontSize: "16px",
				lineHeight: "100%",
				color: expiryStatus.color,
			}}
		>
			{expiryStatus.text}
		</Typography>
	);
};

type QualificationCardProps = {
	qualification: Qualification;
	onShowQualificationClicked: (qualification: Qualification) => void;
	onDeleteQualificationClicked: (qualification: Qualification) => void;
	styleOverride?: CSSProperties;
	isEditable: boolean;
};
export const QualificationCard: FC<QualificationCardProps> = ({
	qualification,
	onShowQualificationClicked,
	onDeleteQualificationClicked,
	styleOverride,
	isEditable,
}) => {
	const handleDeleteClicked = useCallback(() => {
		onDeleteQualificationClicked(qualification);
	}, [onDeleteQualificationClicked, qualification]);

	return (
		<Box
			data-testid="profile-qualification-card"
			style={{
				width: "400px",
				height: "fit-content",
				minHeight: "100px",
				backgroundColor: SURFACE,
				border: `1px solid ${NEUTRAL[150]}`,
				display: "flex",
				alignItems: "start",
				justifyContent: "space-between",
				borderRadius: "4px",
				padding: "0px 4px",
				...styleOverride,
			}}
		>
			<Box
				sx={{
					width: "40px",
				}}
			>
				<ThemedSvg src="/certificate.svg" alt="" size={36} color="var(--inhouse-primary)" />
			</Box>
			<Box
				style={{
					width: "calc(100% - 40px)",
					height: "fit-content",
					minHeight: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					justifyContent: "start",
					padding: "8px",
					gap: "8px",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: isEditable ? "space-between" : "start",
						width: "100%",
					}}
				>
					<Typography
						style={{
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: "100%",
							color: alpha(INK.SECONDARY, 0.5),
						}}
					>
						{qualification.issuer}
					</Typography>
					{isEditable && (
						<IconButton data-testid="profile-delete-qualification-button" onClick={handleDeleteClicked}>
							<DeleteIcon />
						</IconButton>
					)}
				</Box>
				<Typography
					style={{
						fontWeight: 600,
						fontSize: "20px",
						lineHeight: "100%",
						color: INK.SECONDARY,
					}}
				>
					{qualification.title}
				</Typography>
				<QualificationValidity expires={qualification.expires ? DateTime.fromISO(qualification.expires) : null} />
			</Box>
		</Box>
	);
};
